import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { B2CPolicies, MSALConfig } from '../models/auth-config.model';

interface IdTokenClaims extends AuthenticationResult {
  idTokenClaims: {
    tfp?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private readonly _destroying$ = new Subject<void>();

  private token: BehaviorSubject<any> = new BehaviorSubject(null);
  setToken(_token: string) {
    this.token.next(_token);
  }
  get tokenValue() {
    return this.token?.value ?? '';
  }

  private _isLogged: BehaviorSubject<boolean> = new BehaviorSubject(null);
  setIsLogged(isLogged: boolean) {
    this._isLogged.next(isLogged);
  }
  get isLogged(): Observable<boolean> {
    return this._isLogged.asObservable();
  }

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private authService: MsalService
  ) {
    this.broadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS || result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          const payload: IdTokenClaims = result.payload as AuthenticationResult;
          if (payload.idTokenClaims?.tfp?.toLowerCase() === B2CPolicies().names.forgotPassword?.toLowerCase()) {
            window.alert('Password has been reset successfully. \nPlease sign-in with your new password.');
            return this.logout();
          }
          if (!this.token) this.setToken(payload.accessToken);
        }
        if (result.eventType === EventType.LOGIN_FAILURE || result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          if (!!result.error?.message?.includes('AADB2C90118')) {
            const resetPasswordFlowRequest: RedirectRequest = {
              scopes: ['openid'],
              authority: B2CPolicies().authorities.forgotPassword.authority,
              correlationId: undefined
            };
            this.login(resetPasswordFlowRequest);
          } else {
            this.logout();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  login(userFlowRequest?: RedirectRequest) {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest).subscribe();
    } else {
      this.authService.loginRedirect(userFlowRequest).subscribe();
    }
  }

  logout(redirectUrl?: string) {
    this.authService.logoutRedirect({
      postLogoutRedirectUri: redirectUrl || MSALConfig().auth.redirectUri
    });
  }
}
